import { NavigationEnd, Router } from '@angular/router';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { distinctUntilChanged, filter, Subject } from 'rxjs';
import { AppData, AppUpdateAvailableEvent, IAppUpdateAdapter } from './update-adapter';

export class WebSwUpdateAdapter implements IAppUpdateAdapter {
  private readonly updateAvailableSubject = new Subject<AppUpdateAvailableEvent>();
  readonly updateAvailable$ = this.updateAvailableSubject.asObservable();

  constructor(
    private readonly swUpdate: SwUpdate,
    private readonly router: Router,
  ) {}

  initialize(): void {
    if (!this.swUpdate.isEnabled) {
      console.log('[PWA] Service Worker is not enabled');
      return;
    }

    this.router.events
      .pipe(filter((event: unknown): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkForUpdate().catch((error: unknown) => {
          console.error('[PWA] Failed to check for updates:', error);
        });
      });

    this.swUpdate.versionUpdates
      .pipe(
        filter((event: VersionEvent): event is VersionReadyEvent => event.type === 'VERSION_READY'),
        distinctUntilChanged((a, b) => a.latestVersion.hash === b.latestVersion.hash),
      )
      .subscribe((event: VersionReadyEvent) => {
        console.log('[PWA] New version available:', event.currentVersion.hash, '->', event.latestVersion.hash);

        this.updateAvailableSubject.next({
          currentHash: event.currentVersion.hash,
          newHash: event.latestVersion.hash,
          appData: event.latestVersion.appData as AppData,
        });
      });
  }

  checkForUpdate(): Promise<boolean> {
    if (!this.swUpdate.isEnabled) {
      return Promise.resolve(false);
    }
    return this.swUpdate.checkForUpdate();
  }

  downloadUpdate(): Promise<void> {
    return Promise.resolve();
  }

  applyUpdate(): Promise<void> {
    return this.swUpdate.activateUpdate().then(() => undefined);
  }

  restartApp(): Promise<void> {
    document.location.reload();
    return Promise.resolve();
  }
}
